<template>
	<FormCard class="">
		<h3>Life Cycle Breakdown - per {{ calculationOptions?.outputQuantity }} {{ selectedUnit?.unitName }} of {{ outputDescription }}</h3>
		<div class="flex">
			<ag-grid-vue
				:theme="resultGridTheme"
				style="width: 100%; min-height: 350px"
				:rowData="lifeCycleResultList"
				:columnDefs="agGridColumnDefs"
				:loadingOverlayComponentParams="{ loadingMessage: 'Loading result by step. Please wait.' }"
				:noRowsOverlayComponentParams="{ noRowsMessageFunc: () => 'No result by step found.' }"
				:overlayLoadingTemplate="'<span class=&quot;ag-overlay-loading-center&quot;>Loading result by step. Please wait.</span>'"
				:overlayNoRowsTemplate="'<span class=&quot;ag-overlay-no-rows-center&quot;>No result by step found.</span>'"
				:context="{}"
				:loadThemeGoogleFonts="true"
				:defaultColDef="{ wrapHeaderText: true, autoHeaderHeight: true }"
				:pinnedBottomRowData="pinnedBottomRow"
				@grid-ready="onGridReady"
			>
			</ag-grid-vue>
		</div>

		<!-- Chart Type Toggle -->
		<div class="flex mt-2 mb-2 justify-center">
			<div class="flex items-center space-x-4">
				<span class="text-sm font-medium" :class="{ 'text-blue-600': !showPieChart, 'text-gray-500': showPieChart }">Bar Chart</span>
				<label class="relative inline-flex items-center cursor-pointer">
					<input type="checkbox" v-model="showPieChart" class="sr-only peer" />
					<div
						class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"
					></div>
				</label>
				<span class="text-sm font-medium" :class="{ 'text-blue-600': showPieChart, 'text-gray-500': !showPieChart }">Pie Chart</span>
			</div>
		</div>
		<!-- Dynamic Charts Component with CSS Grid Layout -->
		<div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
			<DynamicChartComponent
				v-for="emissionFactor in activeEmissionFactors"
				:key="emissionFactor.id"
				:title="
					props.calculationOptions?.useWeightedEmissionFactors
						? emissionFactor.emissionFactorName?.replace(/ \[.*\]$/, ' [mPt]')
						: emissionFactor.emissionFactorName
				"
				:emissionFactors="emissionFactor"
				:chartData="chartDataItems"
				:showPieChart="showPieChart"
				class="min-h-96 flex flex-col"
			/>
		</div>

		<!-- <div>
			<div>
				<vue-json-pretty :data="lifeCycleResultList" />
			</div>
		</div> -->
	</FormCard>
</template>
<script setup lang="ts">
import { ref, onMounted, computed } from "vue"
import { ApiClient, CalculationOptions, EmissionFactors, LifeCycleResult, Step, Unit } from "../../entities/NSwagGenerated"
import { useToast } from "primevue/usetoast"
import { EventBus } from "@/eventBus"
import { library } from "@fortawesome/fontawesome-svg-core"
import { faInfoCircle, faTrash, faPen, faFloppyDisk, faXmarkLarge } from "@fortawesome/pro-regular-svg-icons"
import DynamicChartComponent from "./DynamicChartComponent.vue"
import type { ChartDataItem } from "./DynamicChartComponent.vue"

library.add(faTrash, faPen, faInfoCircle, faFloppyDisk, faXmarkLarge)

const apiClient = new ApiClient(import.meta.env.VITE_API_URL.toString())

const props = defineProps({
	assessmentId: {
		type: String,
		required: false,
		default: "",
	},
	tabId: {
		type: String,
		required: false,
		default: "",
	},
	activeEmissionFactors: {
		type: Array<EmissionFactors>,
		required: false,
		default: () => [],
	},
	calculationOptions: {
		type: CalculationOptions,
		required: false,
		default: undefined,
	},
	selectedStep: {
		type: Step,
		required: false,
		default: undefined,
	},
	selectedUnit: {
		type: Unit,
		required: false,
		default: undefined,
	},
	tabName: {
		type: String,
		required: false,
		default: "",
	},
	steps: {
		type: Array as () => Step[],
		required: false,
		default: () => [],
	},
})

import { useStepOutputDescription } from "@/composables/stepOutputDescription"
const outputDescription = useStepOutputDescription(
	computed(() => props.tabName),
	computed(() => props.selectedStep),
	computed(() => props.steps as Step[])
)

function getResults(generalInfomationId: string) {
	getLifeCycleResultByGeneralInformationId(generalInfomationId)
}

const getGridApi = () => {
	return gridApi.value
}

defineExpose({
	getResults,
	getGridApi,
	getResultData: () => lifeCycleResultList.value,
})

let toast = useToast()
const displayStepList = ref(false)
const displayLifeCycleResultListGraph1 = ref(false)

const lifeCycleResultList = ref([] as LifeCycleResult[])
const showPieChart = ref(true)

onMounted(() => {})

import { AgGridVue } from "ag-grid-vue3"
import {
	getConditionalFormatColor,
	valueToStringFormatter,
	resultGridTheme,
	ColumnMinMax,
	BuildMinMaxValuesFromEmissionFactors,
} from "../composables/resultGridHelper"
const columnMinMax = ref<Array<ColumnMinMax>>([])

import { CellStyle, GridApi, GridReadyEvent, ValueGetterParams, ColDef } from "ag-grid-community"
const gridApi = ref<GridApi | null>(null)

// Computed property to map lifeCycleResultList to ChartDataItem format
const chartDataItems = computed((): ChartDataItem[] => {
	return lifeCycleResultList.value.map((item) => ({
		name: item.name || "",
		emissionFactors: item.emissionFactors || {},
	}))
})

const pinnedBottomRow = computed(() => {
	if (!lifeCycleResultList.value.length) return []

	const totals = {
		name: "Grand Total",
		totalWeightedScore: 0,
		emissionFactors: {} as { [key: string]: number },
	}

	for (const row of lifeCycleResultList.value) {
		totals.totalWeightedScore += row.totalWeightedScore || 0
		if (row.emissionFactors) {
			for (const ef of props.activeEmissionFactors) {
				if (!ef.emissionFactorName) continue
				totals.emissionFactors[ef.emissionFactorName] =
					(totals.emissionFactors[ef.emissionFactorName] || 0) + (row.emissionFactors[ef.emissionFactorName] || 0)
			}
		}
	}

	return [totals]
})

function onGridReady(params: GridReadyEvent) {
	gridApi.value = params.api
}

const agGridColumnDefs = computed(() => [
	{ field: "name", headerName: "Life Cycle Stage", pinned: "left" as const },
	...(props.calculationOptions?.useWeightedEmissionFactors
		? [
				{
					field: "totalWeightedScore",
					headerName: "Total Weighted Score [mPt]",
					valueGetter: (p: ValueGetterParams) => {
						return p.data?.totalWeightedScore ?? null
					},
					valueFormatter: (p: any) => valueToStringFormatter(p.value, false),
					aggFunc: "sum",
				},
			]
		: []),
	...props.activeEmissionFactors.map<ColDef>((ef) => ({
		field: ef.emissionFactorName,
		headerName: props.calculationOptions?.useWeightedEmissionFactors ? ef.emissionFactorName?.replace(/ \[.*\]$/, " [mPt]") : ef.emissionFactorName,
		valueGetter: (p) => {
			const value = ef.emissionFactorName == null || !p.data?.emissionFactors ? null : (p.data?.emissionFactors[ef.emissionFactorName] ?? null)
			return value
		},
		valueFormatter: (p) => valueToStringFormatter(p.value, false),
		cellStyle: (params): CellStyle | null => {
			// Don't apply conditional formatting to pinned rows
			if (params.node.rowPinned) {
				return { "text-align": "right" }
			}
			let minMax = columnMinMax.value.find((item) => item.columnName === ef.emissionFactorName)
			if (minMax) {
				return {
					"background-color": getConditionalFormatColor(params.value, minMax.min, minMax.max),
					"text-align": "right",
				}
			}
			return null
		},
		aggFunc: "sum",
	})),
])

function getLifeCycleResultByGeneralInformationId(generalInformationId: string) {
	if (generalInformationId != null) {
		apiClient
			.getProductProcessLifeCycleResultByGeneralInformationId(generalInformationId, props.calculationOptions)
			.then(
				(res) => {
					if (res && res != null && res.length > 0) {
						lifeCycleResultList.value = res.filter((row) => row.name?.toLowerCase() !== "total")
						displayStepList.value = true
						displayLifeCycleResultListGraph1.value = true
						var emissionFactors = lifeCycleResultList.value.map((item) => item.emissionFactors ?? {})
						columnMinMax.value = BuildMinMaxValuesFromEmissionFactors(emissionFactors)
					}
				},
				(err) => {
					if (err.status == 400) {
						toast.add({ severity: "error", summary: `Error: ${err.response.message}`, life: 5000 })
						console.error("Error Id:", err.response.errorId)
					} else {
						toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
					}
				}
			)
			.then(() => {
				EventBus.emit("readyToSaveResult_" + props.tabId)
			})
	}
}
</script>
<style></style>
